import { DayOfWeek } from "../types/common.types";

// Day of week order (Monday-based)
export const dayOfWeekOrder: DayOfWeek[] = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

// Day labels for display
export const dayLabels: Record<DayOfWeek, string> = {
  sunday: "Sun",
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
};

/**
 * Format a date as YYYY-MM-DD string (using local timezone)
 */
export const formatDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Check if a date is today
 */
export const isToday = (date: Date): boolean => {
  const today = new Date();
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};

/**
 * Check if a date is in the past (before today)
 */
export const isPastDate = (date: Date): boolean => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const checkDate = new Date(date);
  checkDate.setHours(0, 0, 0, 0);
  return checkDate < today;
};

/**
 * Get dates for a week starting from the given date
 */
export const getWeekDates = (weekStart: Date): Date[] => {
  const dates: Date[] = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date(weekStart);
    date.setDate(weekStart.getDate() + i);
    dates.push(date);
  }
  return dates;
};

/**
 * Get the Monday of the week containing the given date
 */
export const getWeekStart = (date: Date): Date => {
  const day = date.getDay();
  const diff = day === 0 ? -6 : -(day - 1);
  const start = new Date(date);
  start.setDate(date.getDate() + diff);
  start.setHours(0, 0, 0, 0);
  return start;
};

/**
 * Get ISO week number for a date
 */
export const getWeekNumber = (date: Date): number => {
  const d = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
  );
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
};

/**
 * Format a date range for display (e.g., "5-11 Jan 2026" or "28 Dec - 3 Jan 2026")
 */
export const formatDateRange = (startDate: string, endDate: string): string => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const startDay = start.getDate();
  const endDay = end.getDate();
  const startMonth = start.toLocaleDateString("en-US", { month: "short" });
  const endMonth = end.toLocaleDateString("en-US", { month: "short" });
  const year = end.getFullYear();

  if (startMonth === endMonth) {
    return `${startDay}-${endDay} ${startMonth} ${year}`;
  }
  return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${year}`;
};

/**
 * Calculate end time from start time and duration
 */
export const calculateEndTime = (
  startTime: string,
  durationHours: number
): string => {
  const [hours, minutes] = startTime.split(":").map(Number);
  const totalMinutes = hours * 60 + minutes + durationHours * 60;
  const endHours = Math.floor(totalMinutes / 60) % 24;
  const endMinutes = totalMinutes % 60;
  return `${String(endHours).padStart(2, "0")}:${String(endMinutes).padStart(
    2,
    "0"
  )}`;
};

/**
 * Generate a unique key for tracking schedule changes
 */
export const getChangeKey = (
  shopId: string,
  employeeId: string,
  dateKey: string
): string => `${shopId}_${employeeId}_${dateKey}`;

/**
 * Generate a unique key for tracking working hours errors
 */
export const getErrorKey = (
  shopId: string,
  employeeId: string,
  dateKey: string,
  whId: string
): string => `${shopId}_${employeeId}_${dateKey}_${whId}`;

/**
 * Parse a change key back to its components
 */
export const parseChangeKey = (
  key: string
): { shopId: string; employeeId: string; dateKey: string } => {
  const [shopId, employeeId, dateKey] = key.split("_");
  return { shopId, employeeId, dateKey };
};

/**
 * Generate a unique ID for working hours entries
 */
export const generateWorkingHoursId = (): string => {
  return `wh_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};
